/**
 * ============================================================================
 * FHIR error surfacing — TV-P1-07
 * ============================================================================
 *
 * Centralizes the HTTP-code → friendly-copy mapping for FHIR client
 * errors so every UI surface (PatientPanel, CareGapsTab, ProviderQueue,
 * LiveVisit, future tabs) shows the same message + action for the same
 * underlying failure mode.
 *
 * Two render shapes:
 *   <FhirErrorState />  — full-pane error (loading/empty replacement)
 *   <FhirErrorBanner /> — inline banner (above a button, inside a card)
 *
 * The standalone FHIR client (src/fhir/client.js) attaches
 * `error.status` to thrown errors; that's the only thing this module
 * needs to switch on. fhirclient-thrown errors during EHR mode also
 * surface a `.status` via fhirclient's error wrapper.
 * ============================================================================
 */

import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Maps a FHIR client error to a UI-renderable shape:
 *   { title, body, severity, retryable }
 *
 * - title:     short summary shown in bold
 * - body:      one-line technical detail / next step
 * - severity:  'error' | 'warning' | 'info' — drives color
 * - retryable: true if showing a Retry button makes sense
 */
export function describeFhirError(error) {
  if (!error) {
    return {
      title: 'Something went wrong.',
      body: 'No error details available.',
      severity: 'error',
      retryable: false,
    };
  }

  const status = error?.status;
  const fhirDiag =
    error?.data?.issue?.[0]?.diagnostics
    || error?.data?.message
    || error?.message;

  switch (status) {
    case 401:
      return {
        title: 'Session expired.',
        body: 'Your access token is no longer valid. Re-launch this visit from your EHR to refresh.',
        severity: 'error',
        retryable: false,
      };
    case 403:
      return {
        title: "You don't have access to this resource.",
        body:
          'Check that the practitioner has permission on this tenant and that the FHIR scopes on the launch token are correct.',
        severity: 'error',
        retryable: false,
      };
    case 404:
      return {
        title: 'No FHIR data found.',
        body: "The requested resource wasn't found in the active tenant — it may have been deleted or never existed.",
        severity: 'warning',
        retryable: true,
      };
    case 409:
      return {
        title: 'Update conflict.',
        body:
          'Another writer changed this resource between the read and the write. Reload the page and try again.',
        severity: 'warning',
        retryable: true,
      };
    case 410:
      return {
        title: 'Resource was deleted.',
        body: 'This record has been removed from the FHIR server.',
        severity: 'warning',
        retryable: false,
      };
    case 422:
      return {
        title: 'FHIR validation failed.',
        body: fhirDiag || 'The server rejected the resource body. See console for the OperationOutcome.',
        severity: 'error',
        retryable: false,
      };
    case 429:
      return {
        title: 'Server is busy.',
        body: 'The FHIR server is rate-limiting requests. Wait a moment, then try again.',
        severity: 'warning',
        retryable: true,
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        title: 'FHIR server is unavailable.',
        body: 'The server returned a 5xx error. This is usually transient — try again in a moment.',
        severity: 'error',
        retryable: true,
      };
    default:
      if (status && status >= 500) {
        return {
          title: 'FHIR server error.',
          body: fhirDiag || `HTTP ${status}`,
          severity: 'error',
          retryable: true,
        };
      }
      if (status && status >= 400) {
        return {
          title: 'Request rejected.',
          body: fhirDiag || `HTTP ${status}`,
          severity: 'error',
          retryable: false,
        };
      }
      return {
        title: 'Could not reach the FHIR server.',
        body: fhirDiag || 'Check your network connection and try again.',
        severity: 'error',
        retryable: true,
      };
  }
}

// ── Components ─────────────────────────────────────────────────────────────

/**
 * Full-pane error state. Drop in where a `<Loader />` or empty list
 * would normally render.
 */
export function FhirErrorState({ error, onRetry }) {
  const { title, body, severity, retryable } = describeFhirError(error);
  const tint = severity === 'warning' ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center text-slate-400">
      <AlertCircle className={`w-7 h-7 mb-3 ${tint}`} />
      <p className="text-sm font-medium text-white mb-1">{title}</p>
      <p className="text-xs text-slate-500 max-w-xs">{body}</p>
      {retryable && onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 flex items-center gap-2 px-3 py-1.5 text-xs text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg"
        >
          <RefreshCw size={12} />
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Inline banner — fits above a button or inside a card body.
 */
export function FhirErrorBanner({ error, onDismiss }) {
  const { title, body, severity } = describeFhirError(error);
  const palette =
    severity === 'warning'
      ? {
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/20',
        icon: 'text-amber-400',
        title: 'text-amber-200',
        body: 'text-amber-200/80',
      }
      : {
        bg: 'bg-red-500/10',
        border: 'border-red-500/20',
        icon: 'text-red-400',
        title: 'text-red-200',
        body: 'text-red-200/80',
      };

  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border ${palette.bg} ${palette.border}`}>
      <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${palette.icon}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${palette.title}`}>{title}</p>
        <p className={`text-xs ${palette.body}`}>{body}</p>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-slate-500 hover:text-slate-300 text-xs px-1"
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}
